import { useEffect, useState } from "react";
import { Lock, Plus, Trash2, Edit3, X, Save } from "lucide-react";
import { toast } from "sonner";
import { api, adminApi } from "@/lib/api";

export default function BlogAdmin() {
  const [authed, setAuthed] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [adminKey, setAdminKey] = useState("");
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const blank = { title: "", excerpt: "", content: "", cover_image: "", category: "General", author: "Dr. Priyanka Gupta", published: true };
  const [form, setForm] = useState(blank);

  useEffect(() => {
    const saved = localStorage.getItem("admin_key");
    if (saved) {
      verify(saved);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPosts = async () => {
    try {
      const r = await api.get("/blog?include_unpublished=true");
      setPosts(r.data || []);
    } catch {
      setPosts([]);
    }
  };

  const verify = async (k) => {
    try {
      await adminApi(k).post("/admin/verify");
      setAdminKey(k);
      setAuthed(true);
      localStorage.setItem("admin_key", k);
      loadPosts();
    } catch {
      toast.error("Invalid admin key");
      localStorage.removeItem("admin_key");
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    verify(keyInput.trim());
  };

  const logout = () => {
    setAuthed(false);
    setAdminKey("");
    localStorage.removeItem("admin_key");
  };

  const startNew = () => {
    setEditing(null);
    setForm(blank);
    setShowForm(true);
  };

  const startEdit = (post) => {
    setEditing(post);
    setForm({
      title: post.title,
      excerpt: post.excerpt || "",
      content: post.content,
      cover_image: post.cover_image || "",
      category: post.category || "General",
      author: post.author || "Dr. Priyanka Gupta",
      published: post.published,
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    try {
      if (editing) {
        await adminApi(adminKey).put(`/blog/${editing.id}`, form);
        toast.success("Post updated");
      } else {
        await adminApi(adminKey).post("/blog", form);
        toast.success("Post created");
      }
      setShowForm(false);
      setEditing(null);
      setForm(blank);
      loadPosts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save post");
    }
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"?`)) return;
    try {
      await adminApi(adminKey).delete(`/blog/${post.id}`);
      toast.success("Post deleted");
      loadPosts();
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-6 py-24" data-testid="admin-login">
        <div className="bg-white rounded-2xl p-8 border border-[#E8E2D9]">
          <div className="w-12 h-12 rounded-full bg-[#FCEAE4] flex items-center justify-center mb-5">
            <Lock className="w-5 h-5 text-[#EF734B]" />
          </div>
          <h1 className="font-heading text-3xl text-[#484848]">Blog Admin</h1>
          <p className="text-sm text-[#484848]/70 mt-2">Enter your admin key to manage posts.</p>
          <form onSubmit={handleLogin} className="mt-6 space-y-4">
            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="Admin key"
              data-testid="admin-key-input"
              className="w-full px-4 py-3 rounded-lg border border-[#E8E2D9] bg-[#FAF7F2] focus:outline-none focus:border-[#EF734B] focus:ring-1 focus:ring-[#EF734B]"
            />
            <button type="submit" data-testid="admin-login-btn" className="btn-primary w-full justify-center">Sign In</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="max-w-6xl mx-auto px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="overline mb-2">Admin</div>
          <h1 className="font-heading text-4xl text-[#484848]">Blog Posts</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={startNew} data-testid="admin-new-post-btn" className="btn-primary text-sm">
            <Plus className="w-4 h-4" /> New Post
          </button>
          <button onClick={logout} data-testid="admin-logout-btn" className="btn-outline text-sm">Logout</button>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-[#484848]/50 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto" data-testid="admin-form-modal">
          <div className="bg-white rounded-2xl max-w-3xl w-full p-8 my-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl text-[#484848]">{editing ? "Edit Post" : "New Post"}</h2>
              <button onClick={() => setShowForm(false)} className="w-9 h-9 rounded-full hover:bg-[#FAF7F2] flex items-center justify-center" data-testid="admin-close-form">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <Field label="Title">
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="admin-title" required />
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Category">
                  <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="admin-category" />
                </Field>
                <Field label="Author">
                  <input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className={inputCls} data-testid="admin-author" />
                </Field>
              </div>
              <Field label="Cover Image URL (optional)">
                <input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className={inputCls} data-testid="admin-cover" placeholder="https://..." />
              </Field>
              <Field label="Excerpt">
                <textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={`${inputCls} h-20`} data-testid="admin-excerpt" />
              </Field>
              <Field label="Content (paragraphs separated by blank line)">
                <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className={`${inputCls} h-64 font-mono text-sm`} data-testid="admin-content" required />
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="admin-published" />
                Published
              </label>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowForm(false)} className="btn-outline text-sm">Cancel</button>
                <button type="submit" className="btn-primary text-sm" data-testid="admin-save-btn"><Save className="w-4 h-4" /> {editing ? "Update" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden">
        {posts.length === 0 ? (
          <div className="p-12 text-center text-[#484848]/60">No posts yet. Create your first.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-[#FAF7F2]">
              <tr className="text-left text-xs uppercase tracking-wider text-[#484848]/60">
                <th className="px-6 py-4">Title</th>
                <th className="px-6 py-4">Category</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id} className="border-t border-[#E8E2D9]" data-testid={`admin-row-${p.slug}`}>
                  <td className="px-6 py-4 font-medium text-[#484848]">{p.title}</td>
                  <td className="px-6 py-4 text-sm text-[#484848]/70">{p.category}</td>
                  <td className="px-6 py-4">
                    <span className={`text-xs px-2 py-1 rounded-full ${p.published ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                      {p.published ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-[#484848]/70">
                    {new Date(p.created_at).toLocaleDateString("en-IN")}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => startEdit(p)} className="text-[#484848] hover:text-[#EF734B] mr-4" data-testid={`admin-edit-${p.slug}`}>
                      <Edit3 className="w-4 h-4 inline" />
                    </button>
                    <button onClick={() => remove(p)} className="text-[#484848] hover:text-red-600" data-testid={`admin-delete-${p.slug}`}>
                      <Trash2 className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const inputCls = "w-full px-4 py-2.5 rounded-lg border border-[#E8E2D9] bg-white focus:outline-none focus:border-[#EF734B] focus:ring-1 focus:ring-[#EF734B] text-sm";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-[#484848]/70 font-semibold mb-1.5">{label}</label>
      {children}
    </div>
  );
}
